import type { Job, JobStatus } from '@jobos/shared';
import { cn } from '@/lib/utils';
import { billingBarSegments, type BarSegment } from './billingBarSegments';

export interface BillingBarProps {
  summary: Job['jobBillingSummary'];
  jobStatus: JobStatus;
  className?: string;
}

const SEGMENT_CLASS: Record<BarSegment['kind'], string> = {
  paid: 'bg-[var(--billing-paid)]',
  billed: 'bg-[var(--billing-billed)]',
  tobill: 'bg-[var(--billing-tobill)]',
  running: 'bg-[var(--billing-running)]',
};

const SEGMENT_LABEL: Record<BarSegment['kind'], string> = {
  paid: 'collected',
  billed: 'invoiced and unpaid',
  tobill: 'still to bill',
  running: 'not billed yet',
};

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// Not `describe` — that is the Vitest global everywhere else in this repo.
const describeSegment = (s: BarSegment) => `${money(s.raw)} ${SEGMENT_LABEL[s.kind]}`;

/**
 * How far through billing a job is: collected, invoiced-not-collected, and
 * still-to-bill, as one 5px line. Proportional only — it answers "how far
 * along", not "how much". Amounts are in each segment's title and in the
 * aria-label, which is the only place the total appears.
 *
 * Renders an empty track when the job has no active breakdown document: there
 * is no denominator, and a bar scaled to what was invoiced would read as fully
 * billed. See billingBarSegments.
 */
export function BillingBar({ summary, jobStatus, className }: BillingBarProps) {
  const segments = billingBarSegments(summary, jobStatus);
  const total = summary && summary.contractValue !== null
    ? Math.max(summary.contractValue, summary.invoiced)
    : null;

  const label = segments.length && total !== null
    ? `Billing: ${segments.map(describeSegment).join(', ')}, of ${money(total)}`
    : 'Billing: no breakdown document, nothing to measure';

  return (
    <span
      role="img"
      aria-label={label}
      className={cn('flex h-[5px] w-full overflow-hidden rounded-[3px] bg-[var(--billing-track)]', className)}
    >
      {segments.map((s, i) => (
        <span
          key={`${s.kind}-${i}`}
          data-segment={s.kind}
          title={describeSegment(s)}
          className={SEGMENT_CLASS[s.kind]}
          style={s.fill ? { flex: '1 1 auto' } : { flex: `0 0 ${s.pct}%` }}
        />
      ))}
    </span>
  );
}
